import {
  Box,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  SwipeableDrawer,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormSelect from 'components/common/Select/FormSelect';
import { FormikProps, useFormik } from 'formik';
import { Dispatch, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';
import { IAdminOppFilters, IOpportunity, ISearchOpp, OpportunityLocationType } from 'global/interfaces/opportunity';
import { mapToSearchParams } from 'utils/search';
import { getAdminOpportunities, getOpportunities } from 'services/opportunityService';
import { showError } from 'utils/errorHandler';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { GigFilterForm } from './GigFilterForm';
import { PagedResult } from 'global/interfaces/PagedResult';
import { setFilters } from 'services/filterService';

interface IOppFilterProps {
  children?: React.ReactNode;
  setLoading: Dispatch<boolean>;
  setPagedResult: Dispatch<PagedResult<IOpportunity[]>>;
  refresh?: boolean;
  isAdminComponent?: boolean;
  adminOppFilters?: IAdminOppFilters;
  onDrawerClose?: () => void;
  setIsFilterApplied?: Dispatch<boolean>;
  filterOpen?: boolean;
}

export const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    paddingRight: 0,
  },
  fieldset: {
    borderRadius: '12px',
  },
}));

export const StyledSelectField = styled(FormSelect)(({ theme }) => ({
  borderRadius: '12px',
  fieldset: {
    borderColor: theme.palette.grey[200],
    borderRadius: '12px',
  },
}));

export default function OppFilters(props: IOppFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialLoad, setInitialLoad] = useState(true);
  const authContext = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (authContext.user && authContext.user.filterQuery && !props.isAdminComponent) {
      const params = new URLSearchParams(authContext.user.filterQuery);
      props.setIsFilterApplied?.(isFilterApplied(params));
      setSearchParams(params, { replace: true });
    }
  }, [authContext.user?.filterQuery]);

  const form = useFormik<ISearchOpp>({
    initialValues: {
      categories: searchParams.getAll('categories'),
      search: searchParams.get('search') ?? null,
      countries:
        searchParams.has('countries') && !props.isAdminComponent
          ? searchParams.getAll('countries')
          : authContext.user?.country && !props.isAdminComponent
          ? [authContext.user?.country ?? '']
          : [],
      roleTypes:
        searchParams.getAll('roleTypes').length > 0
          ? searchParams.getAll('roleTypes')
          : [OpportunityLocationType.Remote, OpportunityLocationType.Hybrid, OpportunityLocationType.Local],
      free: searchParams.get('free') === 'true',
      pageNumber: 1,
    },
    enableReinitialize: true,
    onSubmit: values => {
      props.setLoading(true);
      props.onDrawerClose?.();
      const params = mapToSearchParams(values);
      if (!props.isAdminComponent) {
        fetchUserOpps(params);
        updateFilters(params);
        return;
      }

      fetchAdminOpps(values);
    },
  });

  useEffect(() => {
    if (!authContext.loading) {
      form.submitForm();
    }
  }, [props.refresh, authContext.loading, props.adminOppFilters]);

  const fetchUserOpps = (params: URLSearchParams) => {
    getOpportunities(params)
      .then(res => {
        props.setPagedResult(res);
      })
      .catch(showError)
      .finally(() => props.setLoading(false));
  };

  const fetchAdminOpps = (values: ISearchOpp) => {
    if (!props.isAdminComponent || !props.adminOppFilters) return;
    const params: URLSearchParams = mapToSearchParams({ ...props.adminOppFilters, ...values });

    getAdminOpportunities(params)
      .then((retrievedOpps: IOpportunity[]) => {
        const pagedResult: PagedResult<IOpportunity[]> = {
          data: retrievedOpps,
          total: retrievedOpps.length,
        };
        props.setPagedResult(pagedResult);
      })
      .catch(showError)
      .finally(() => {
        props.setLoading(false);
        setSearchParams(params);
      });
  };

  const updateFilters = (params: URLSearchParams) => {
    if (!authContext.user || props.isAdminComponent) {
      setSearchParams(params);
      return;
    }

    if (initialLoad) {
      setInitialLoad(false);
      return;
    }

    setFilters(params).then(() => {
      authContext.setFilterQuery(params);
      enqueueSnackbar({
        variant: 'success',
        message: 'Saved to your gigs page, Slack alerts and daily emails',
        autoHideDuration: 3000,
        content: (key, message) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: isMobile ? '8px 12px' : '12px 16px',
              bgcolor: '#252A62',
              color: 'success.contrastText',
              borderRadius: 1,
            }}
          >
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ color: 'grey.50' }}>
              {message}
            </Typography>
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              onClick={() => closeSnackbar(key)}
              sx={{
                ml: isMobile ? 1 : 2,
                p: isMobile ? 0.5 : 1,
                color: 'grey.50',
              }}
            >
              <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Box>
        ),
      });
    });
  };

  return (
    <>
      {props.isAdminComponent || !isMobile ? (
        <GigFilterForm form={form} onDrawerClose={props.onDrawerClose}>
          {props.children}
        </GigFilterForm>
      ) : (
        <MobileGigFilterDrawer {...props} form={form} />
      )}
    </>
  );
}

interface IMobileFilterProps extends IOppFilterProps {
  form: FormikProps<ISearchOpp>;
}

function MobileGigFilterDrawer(props: IMobileFilterProps) {
  if (!props.onDrawerClose) {
    console.error('Drawer close func not found');
    return <></>;
  }

  return (
    <>
      <SwipeableDrawer
        open={props.filterOpen}
        anchor="bottom"
        onClose={props.onDrawerClose}
        onOpen={props.onDrawerClose}
        PaperProps={{
          style: {
            position: 'fixed',
            bottom: '0px',
            borderRadius: '10px 10px 0px 0px',
          },
        }}
      >
        <Stack padding="0px 24px 24px 24px">
          <GigFilterForm form={props.form} onDrawerClose={props.onDrawerClose}>
            {props.children}
          </GigFilterForm>
        </Stack>
      </SwipeableDrawer>
    </>
  );
}

function isFilterApplied(params: URLSearchParams): boolean {
  const vals = Array.from(params.values()).filter(v => v !== 'false');
  return vals.length > 0;
}
